import { useState } from 'react';
import { Section } from '../types';
import Input from './ui/Input';
import Textarea from './ui/Textarea';
import Button from './ui/Button';
import Card from './ui/Card';

interface EditorProps {
  title: string;
  authors: string[];
  affiliations: string[];
  summary: string;
  keyTakeaways: string[];
  sections: Section[];
  onUpdate: (updates: {
    title?: string;
    authors?: string[];
    affiliations?: string[];
    summary?: string;
    keyTakeaways?: string[];
    sections?: Section[];
  }) => void;
}

export default function Editor({
  title,
  authors,
  affiliations,
  summary,
  keyTakeaways,
  sections,
  onUpdate,
}: EditorProps) {
  const [localTitle, setLocalTitle] = useState(title);
  const [localAuthors, setLocalAuthors] = useState(authors.join(', '));
  const [localAffiliations, setLocalAffiliations] = useState(affiliations.join(', '));
  const [localSummary, setLocalSummary] = useState(summary);
  const [localTakeaways, setLocalTakeaways] = useState(keyTakeaways.join('\n'));
  const [localSections, setLocalSections] = useState(sections);

  const handleTitleChange = (value: string) => {
    setLocalTitle(value);
    onUpdate({ title: value });
  };

  const handleAuthorsChange = (value: string) => {
    setLocalAuthors(value);
    onUpdate({ authors: value.split(',').map(a => a.trim()).filter(Boolean) });
  };

  const handleAffiliationsChange = (value: string) => {
    setLocalAffiliations(value);
    onUpdate({ affiliations: value.split(',').map(a => a.trim()).filter(Boolean) });
  };

  const handleSummaryChange = (value: string) => {
    setLocalSummary(value);
    onUpdate({ summary: value });
  };

  const handleTakeawaysChange = (value: string) => {
    setLocalTakeaways(value);
    onUpdate({ keyTakeaways: value.split('\n').filter(Boolean) });
  };

  const handleSectionUpdate = (index: number, field: 'title' | 'content', value: string) => {
    const updated = [...localSections];
    updated[index] = { ...updated[index], [field]: value };
    setLocalSections(updated);
    onUpdate({ sections: updated });
  };

  const addSection = () => {
    const newSection: Section = { title: '', content: '' };
    const updated = [...localSections, newSection];
    setLocalSections(updated);
    onUpdate({ sections: updated });
  };

  const removeSection = (index: number) => {
    const updated = localSections.filter((_, i) => i !== index);
    setLocalSections(updated);
    onUpdate({ sections: updated });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-neutral-900 mb-6">
        Edit Poster Content
      </h2>

      <div className="space-y-6">
        <Input
          label="Title"
          value={localTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
        />

        <Input
          label="Authors (comma-separated)"
          value={localAuthors}
          onChange={(e) => handleAuthorsChange(e.target.value)}
        />

        <Input
          label="Affiliations (comma-separated)"
          value={localAffiliations}
          onChange={(e) => handleAffiliationsChange(e.target.value)}
        />

        <Textarea
          label="Summary"
          value={localSummary}
          onChange={(e) => handleSummaryChange(e.target.value)}
          rows={4}
        />

        <Textarea
          label="Key Takeaways (one per line)"
          value={localTakeaways}
          onChange={(e) => handleTakeawaysChange(e.target.value)}
          rows={5}
        />

        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start 
                          sm:items-center mb-4 gap-4">
            <label className="text-lg font-semibold text-neutral-900">Sections</label>
            <Button variant="success" onClick={addSection}>
            + Add Section
            </Button>
        </div>

          <div className="space-y-4">
        {localSections.map((section, index) => (
              <Card key={index}>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <Input
                value={section.title}
                onChange={(e) => handleSectionUpdate(index, 'title', e.target.value)}
                placeholder="Section title"
                    className="flex-1"
              />
                  <Button
                    variant="danger"
                onClick={() => removeSection(index)}
                    className="sm:w-auto w-full"
              >
                Remove
                  </Button>
            </div>
                <Textarea
              value={section.content}
              onChange={(e) => handleSectionUpdate(index, 'content', e.target.value)}
              placeholder="Section content"
              rows={4}
                />
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
